import { APP_ROUTES } from '@/navigation';
import type { Score } from '@/music/score/types';
import type { VoicingLens } from '@/state/store';

export type TuneWorkspaceMode = 'practise' | 'arrange' | 'edit';

interface LastTuneWorkspace {
  scoreId: string;
  mode: TuneWorkspaceMode;
  arrangeLens?: Exclude<VoicingLens, 'none'>;
}

const KEY = 'vlc:last-tune-workspace';

/** Unsaved drafts have no document ID yet; their opening measure still has
 * a persisted identity, so ordinary title/note edits keep view memory. */
export function tuneWorkspaceKey(score: Pick<Score, 'id' | 'form'>): string | undefined {
  return score.id ?? (score.form[0]?.id ? `draft:${score.form[0].id}` : undefined);
}

/** View memory belongs to this browser tab, not to the score's musical data. */
export function lastTuneWorkspace(scoreId: string | undefined): LastTuneWorkspace | null {
  if (!scoreId) return null;
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? 'null');
    if (!stored || typeof stored !== 'object') return null;
    const value = stored as Partial<LastTuneWorkspace>;
    if (value.scoreId !== scoreId || !['practise', 'arrange', 'edit'].includes(value.mode ?? '')) {
      return null;
    }
    return {
      scoreId,
      mode: value.mode!,
      ...(value.arrangeLens === 'comping' || value.arrangeLens === 'melody'
        ? { arrangeLens: value.arrangeLens }
        : {}),
    };
  } catch {
    return null;
  }
}

export function rememberTuneWorkspace(
  scoreId: string | undefined,
  mode: TuneWorkspaceMode,
  lens: VoicingLens,
): void {
  if (!scoreId) return;
  const previous = lastTuneWorkspace(scoreId);
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        scoreId,
        mode,
        arrangeLens: lens === 'none' ? previous?.arrangeLens : lens,
      }),
    );
  } catch {
    // Navigation remains usable if private browsing disallows tab storage.
  }
}

export function continueTuneWorkspaceHref(scoreId: string | undefined): string {
  return lastTuneWorkspace(scoreId)?.mode === 'edit'
    ? APP_ROUTES.scoreStudio
    : APP_ROUTES.workbench;
}
